import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { cleanSlugInput, normalizeSlug, validateSlug } from "../../lib/slug.js";
import { PUBLIC_BASE_URL } from "../../firebase/config.js";
import { trackMetaPixelPageView } from "../../external/metaPixel.js";

const EVENT_EXAMPLES = [
  { label: "Neuroscience summit", slug: "neurocon2026" },
  { label: "Wedding weekend", slug: "maya-and-jules" },
  { label: "Product launch", slug: "atlas-launch" },
  { label: "Birthday dinner", slug: "nia-turns-30" },
  { label: "Fundraising gala", slug: "brightpath-gala" },
  { label: "Photography workshop", slug: "lightlab-studio" },
];

// Marketing landing + the claim box. User types a desired link; we validate
// then send them to the event-type chooser (which performs the reservation).
export default function Landing() {
  const [raw, setRaw] = useState("");
  const [slugWarning, setSlugWarning] = useState("");
  const [exampleIndex, setExampleIndex] = useState(0);
  const navigate = useNavigate();
  const slug = normalizeSlug(raw);
  const check = validateSlug(slug);
  const host = PUBLIC_BASE_URL.replace(/^https?:\/\//, "");
  const activeExample = EVENT_EXAMPLES[exampleIndex];

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setExampleIndex((current) => (current + 1) % EVENT_EXAMPLES.length);
    }, 2400);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    trackMetaPixelPageView();
  }, []);

  function go(e) {
    e.preventDefault();
    const clean = normalizeSlug(raw);
    const cleanCheck = validateSlug(clean);
    setRaw(clean);
    if (!cleanCheck.ok) return;
    navigate(`/app/claim/${clean}`);
  }

  function updateSlug(value) {
    const cleaned = cleanSlugInput(value);
    setRaw(cleaned.slug);
    setSlugWarning(cleaned.warning);
  }

  return (
    <div className="container" style={{ paddingTop: 80, paddingBottom: 80 }}>
      <div className="center stack" style={{ maxWidth: 720, margin: "0 auto" }}>
        <h1 style={{ fontSize: "clamp(2.4rem,7vw,4.2rem)", lineHeight: 1.05, margin: 0, letterSpacing: "-0.03em" }}>
          One link. <span style={{ color: "var(--app-accent)" }}>A beautiful invite.</span>
        </h1>
        <p className="muted landing-hero-copy" style={{ fontSize: "1.2rem" }}>
          Claim a link for your next
          {" "}
          <span className="landing-example-ticker" aria-live="polite">
            <span
              className="landing-example-track"
              style={{ transform: `translateY(-${exampleIndex * 100}%)` }}
            >
              {EVENT_EXAMPLES.map((example) => (
                <span key={example.slug} className="landing-example-item">
                  {example.label}
                </span>
              ))}
            </span>
          </span>
          .
          <span className="landing-hero-followup">
            <span>From</span>
            {" "}
            <strong>{host}/{activeExample.slug}</strong>
            {" "}
            <span>to a premium, scrollable invite in minutes.</span>
          </span>
        </p>

        <form onSubmit={go} className="card landing-claim-card" style={{ marginTop: 24 }}>
          <label className="label muted" style={{ display: "block", textAlign: "left", marginBottom: 8 }}>
            Pick your link
          </label>
          <div className="landing-claim-row">
            <span className="input landing-host-prefix">
              {host}/
            </span>
            <input
              className="input landing-slug-input"
              autoFocus
              placeholder={activeExample.slug}
              value={raw}
              onChange={(e) => updateSlug(e.target.value)}
            />
            <button className="btn landing-claim-button" type="submit" disabled={!check.ok}>
              Claim
            </button>
          </div>
          {slugWarning && (
            <p className="muted" style={{ textAlign: "left", marginTop: 10 }}>
              {slugWarning}
            </p>
          )}
          {raw && !check.ok && <p className="error" style={{ textAlign: "left", marginTop: 10 }}>{check.reason}</p>}
          {raw && check.ok && (
            <p className="muted" style={{ textAlign: "left", marginTop: 10 }}>
              Looks good — you'll get <strong>{host}/{slug}</strong>
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
